/**
 * Comments Realtime Bridge
 * React component that connects to Ably for real-time comment updates
 */

import { useEffect } from "react";
import { connectCommentsRealtime } from "../services/comments-realtime.service";
import { useCommentsStore } from "../store/comments.store";

interface CommentsRealtimeBridgeProps {
	projectId: string;
}

export function CommentsRealtimeBridge({
	projectId,
}: CommentsRealtimeBridgeProps) {
	// Use individual selectors for stable references (same pattern as CommentsPanel).
	const appendRealtime = useCommentsStore((s) => s.appendRealtime);
	const catchUp = useCommentsStore((s) => s.catchUp);
	const setConnecting = useCommentsStore((s) => s.setConnecting);
	const setProjectId = useCommentsStore((s) => s.setProjectId);
	const reset = useCommentsStore((s) => s.reset);

	useEffect(() => {
		setProjectId(projectId);

		let disconnected = false;
		let hasConnectedOnce = false;
		let cleanup: (() => void) | undefined;

		const start = async () => {
			setConnecting(true);
			try {
				if (disconnected) {
					setConnecting(false);
					return;
				}

				const connection = await connectCommentsRealtime({
					projectId,
					onComment: appendRealtime,
					onConnected: () => {
						setConnecting(false);
						if (hasConnectedOnce) {
							void catchUp();
						}
						hasConnectedOnce = true;
					},
					onError: () => setConnecting(false),
				});

				if (disconnected) {
					connection.disconnect();
					return;
				}

				cleanup = connection.disconnect;
			} catch {
				setConnecting(false);
			}
		};

		void start();

		return () => {
			disconnected = true;
			cleanup?.();
			reset();
		};
	}, [appendRealtime, catchUp, projectId, reset, setConnecting, setProjectId]);

	return null;
}
